import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { filter, Subscription } from 'rxjs';

import { MENU_ITEMS } from '../core/config/menu.config';
import { MenuItem } from '../core/models/menu.model';
import { AuthService } from '../core/auth/services/auth.service';
import { TokenService } from '../core/auth/services/token.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, ButtonModule, AvatarModule, TooltipModule, MenuModule, TranslateModule],
  styles: [
    ':host { display: block; }',
    '.menu-link-active { background-color: rgba(255, 255, 255, 0.12); color: #fff; }',
    ':host ::ng-deep .menu-tooltip { border: 1px solid #123264; box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35); }',
    ':host ::ng-deep .menu-tooltip .p-tooltip-text { background-color: #0b2246; color: #fff; opacity: 1; padding: 10px 12px; border-radius: 10px; font-weight: 600; letter-spacing: 0.01em; }',
    ':host ::ng-deep .menu-tooltip .p-tooltip-arrow { display: none; }',
    ':host ::ng-deep .avatar-menu .p-menu-list { padding: 0.5rem 0; }',
    ':host ::ng-deep .avatar-menu .p-menuitem-link { padding: 0.75rem 1rem; }',
    ':host ::ng-deep .avatar-menu .p-menuitem-text, :host ::ng-deep .avatar-menu .p-menuitem-icon { color: #dc2626 !important; }'
  ],
  template: `
    <div class="flex min-h-screen bg-[var(--bg-soft)] text-slate-800 relative">
      <div
        class="fixed inset-0 bg-black/30 backdrop-blur-sm z-30 lg:hidden"
        *ngIf="!isDesktop && mobileOpen"
        (click)="toggleSidebar()"
        aria-hidden="true"
      ></div>

      <aside
        class="bg-brand-900 text-white flex flex-col fixed inset-y-0 left-0 z-40 transform transition-transform duration-200"
        [class.-translate-x-full]="!isDesktop && !mobileOpen"
        [style.width.px]="asideWidth"
      >
        <div class="flex items-center px-5 py-4 gap-3 border-b border-white/10">
          <span class="pi pi-heart-fill text-xl text-sky-100"></span>
          <div class="leading-tight" *ngIf="!collapseCompact">
            <p class="text-xs uppercase text-white/60 tracking-wide">Gestión biomédica</p>
            <p class="font-semibold">{{ clinic }}</p>
          </div>
        </div>
        <nav class="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <a
            *ngFor="let item of menu; trackBy: trackByLabel"
            [routerLink]="item.path"
            routerLinkActive="menu-link-active"
            class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition hover:bg-white/10 hover:text-white"
            (click)="handleNavClick()"
            pTooltip="{{ item.label }}"
            tooltipStyleClass="menu-tooltip"
            tooltipPosition="right"
            [tooltipDisabled]="!collapseCompact"
          >
            <i [class]="item.icon"></i>
            <span class="flex-1" *ngIf="!collapseCompact">{{ item.label }}</span>
            <span *ngIf="item.badge" class="text-xs bg-white/20 px-2 py-0.5 rounded-full">{{ item.badge }}</span>
          </a>
        </nav>
        <div class="px-4 py-4 border-t border-white/10 flex items-center gap-3" *ngIf="!collapseCompact; else compactUser">
          <p-avatar [label]="userInitials" shape="circle" styleClass="bg-white/10"></p-avatar>
          <div class="text-sm leading-tight flex-1">
            <p class="font-semibold">{{ user.name }}</p>
            <p class="text-white/70">{{ user.role }}</p>
          </div>
        </div>
        <button
          pButton
          type="button"
          icon="pi pi-sign-out"
          class="p-button-text p-button-sm w-full text-white/80 hover:text-white"
          [class.justify-center]="collapseCompact"
          [label]="collapseCompact ? '' : ('actions.logout' | translate)"
          pTooltip="{{ 'actions.logout' | translate }}"
          tooltipStyleClass="menu-tooltip"
          tooltipPosition="right"
          [tooltipDisabled]="!collapseCompact"
          (click)="logout()"
        ></button>
        <div class="px-4 pb-4 pt-2 border-t border-white/10 text-[11px] leading-4 text-white/65" *ngIf="!collapseCompact">
          <p>© {{ currentYear }} JM Biomédica</p>
          <p>
            Desarrollado por
            <a
              href="https://simplified.com.co"
              target="_blank"
              rel="noopener noreferrer"
              class="text-white font-semibold hover:text-sky-200 transition"
            >simplified.com.co</a>
          </p>
        </div>
        <ng-template #compactUser>
          <div class="px-4 py-4 border-t border-white/10 flex items-center justify-center">
            <p-avatar [label]="userInitials" shape="circle" styleClass="bg-white/10"></p-avatar>
          </div>
        </ng-template>
      </aside>

      <div class="flex-1 flex flex-col min-w-0 overflow-hidden" [style.marginLeft.px]="contentMargin">
        <header class="h-14 sm:h-16 bg-white border-b border-[var(--border)] px-2 sm:px-5 lg:px-6 flex items-center justify-between gap-2 sm:gap-3 shadow-sm">
          <div class="flex items-center gap-1 sm:gap-3 text-xs sm:text-sm text-slate-600 min-w-0 flex-1">
            <button
              pButton
              type="button"
              class="p-button-text p-button-rounded p-button-sm"
              icon="pi pi-bars"
              (click)="toggleSidebar()"
              [attr.aria-expanded]="toggleExpanded"
              aria-label="Alternar menú"
            ></button>
            <span class="text-brand-700 font-semibold truncate">{{ currentPageLabel }}</span>
          </div>
          <div class="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <p-avatar [label]="userInitials" shape="circle" styleClass="shadow-sm cursor-pointer hover:ring-2 hover:ring-primary/20 transition" (click)="profileMenuTop.toggle($event)"></p-avatar>
            <p-menu #profileMenuTop [model]="profileMenuItems" [popup]="true" appendTo="body" styleClass="avatar-menu shadow-lg rounded-xl border-none w-48 mt-2"></p-menu>
          </div>
        </header>

        <main class="p-3 sm:p-4 md:p-6">
          <router-outlet></router-outlet>
        </main>
      </div>
    </div>
  `
})
export class ShellComponent implements OnInit, OnDestroy {
  get menu(): MenuItem[] {
    return MENU_ITEMS.filter(item =>
      !item.permission || this.tokenService.hasPermission(item.permission)
    );
  }
  readonly clinic = 'Biomédico';
  readonly currentYear = new Date().getFullYear();
  user = { name: 'Usuario', role: 'Usuario' };
  isDesktop = typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true;
  sidebarCollapsed = false;
  mobileOpen = false;
  currentPageLabel = 'Dashboard';

  private authService = inject(AuthService);
  private tokenService = inject(TokenService);

  get profileMenuItems() {
    const items: { label: string; icon: string; command: () => void }[] = [];
    if (this.tokenService.hasPermission('module:profile')) {
      items.push({
        label: 'Mi perfil',
        icon: 'pi pi-user',
        command: () => { this.router.navigate(['/perfil']); }
      });
    }
    items.push({
      label: 'Cerrar Sesión',
      icon: 'pi pi-sign-out',
      command: () => { this.logout(); }
    });
    return items;
  }

  private mediaQuery: MediaQueryList | null = typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)') : null;
  private mediaListener?: (event: MediaQueryListEvent) => void;
  private routerSub?: Subscription;

  constructor(private readonly router: Router) { }

  get userInitials() {
    return this.buildInitials(this.user.name);
  }

  trackByLabel(_index: number, item: MenuItem) {
    return item.label;
  }

  toggleSidebar() {
    if (this.isDesktop) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
    } else {
      this.mobileOpen = !this.mobileOpen;
    }
  }

  handleNavClick() {
    if (!this.isDesktop) {
      this.mobileOpen = false;
    }
  }

  ngOnInit() {
    if (this.mediaQuery) {
      this.mediaListener = (event: MediaQueryListEvent) => {
        this.isDesktop = event.matches;
        if (event.matches) {
          this.mobileOpen = false;
        } else {
          this.sidebarCollapsed = false;
        }
      };
      this.mediaQuery.addEventListener('change', this.mediaListener);
    }

    const payload = this.tokenService.decodeToken();
    if (payload) {
      const resolvedName = payload.full_name || payload.name || payload.preferred_username || payload.username || 'Usuario';
      this.user = {
        name: resolvedName,
        role: this.formatRoleLabel(payload.roles)
      };
    }

    this.updateBreadcrumbFromUrl(this.router.url);
    this.routerSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateBreadcrumbFromUrl(event.urlAfterRedirects));
  }

  ngOnDestroy() {
    if (this.mediaQuery && this.mediaListener) {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
    }
    this.routerSub?.unsubscribe();
  }

  logout() {
    this.authService.logout();
  }

  private updateBreadcrumbFromUrl(url: string) {
    const segment = url.split('/').filter(Boolean)[0] ?? 'equipos';
    const match = this.menu.find((item) => (item.path ?? '').replace('/', '') === segment);
    this.currentPageLabel = match?.label ?? 'Equipos';
  }

  get collapseCompact() {
    return this.isDesktop && this.sidebarCollapsed;
  }

  get asideWidth() {
    if (this.isDesktop) {
      return this.sidebarCollapsed ? 72 : 256;
    }
    return 256;
  }

  get contentMargin() {
    if (this.isDesktop) {
      return this.sidebarCollapsed ? 72 : 256;
    }
    return 0;
  }

  get toggleExpanded() {
    return this.isDesktop ? !this.sidebarCollapsed : this.mobileOpen;
  }

  private buildInitials(name: string) {
    const parts = name
      .trim()
      .split(/\s+/)
      .filter(Boolean);

    if (parts.length === 0) {
      return 'US';
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  private formatRoleLabel(roles: unknown): string {
    if (Array.isArray(roles) && roles.length > 0) {
      return roles
        .map((role) => String(role).replace(/^ROLE_/, ''))
        .join(', ');
    }

    if (typeof roles === 'string' && roles.trim().length > 0) {
      return roles.replace(/^ROLE_/, '');
    }

    return 'Usuario';
  }
}
